import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The mesh a CSG root draws: it loads the library, memoises the evaluation, mounts one material slot
 * per surface and publishes the status to the subtree. Publishing here, not through CsgPrimitive,
 * spares the root subtree a re-render on each load transition.
 */
import { useEffect, useMemo, useState } from 'react';
import { visualLayersUserData } from '../visualLayers';
import { SurfaceMaterialSlots } from '../materials/SurfaceMaterialSlots';
import { NextPassChain } from '../materials/nextPass';
import { resolveMaterialSource } from '../materials/materialSource';
import { CsgSubtreeProvider } from '../contexts/CsgSubtreeContext';
import { nodeComponentRegistry } from '../NodeComponentRegistry';
import { evaluateCsgPlan } from './evaluateCsgPlan';
import { getCachedEvaluation, setCachedEvaluation } from './csgEvaluationCache';
import { loadCsgModule } from './csgModule';
import { usePendingWhile } from '../../resources/usePendingWhile';
export function CsgRootMesh({ plan, setup: { context, isLoading }, shadow, layers, meshRef, fallback, children, }) {
    const [csg, setCsg] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    useEffect(() => {
        // Effect + state rather than React.lazy/Suspense: <Canvas> mounts its own
        // reconciler root, so a suspended CSG root would blank its SIBLINGS while it
        // waited. CollisionGizmo uses the same idiom for the same reason.
        let cancelled = false;
        loadCsgModule().then((module) => {
            if (!cancelled)
                setCsg(module);
        }, () => {
            if (!cancelled)
                setLoadFailed(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const evaluation = useMemo(() => {
        if (!csg || isLoading)
            return null;
        const cached = getCachedEvaluation(plan.cacheKey);
        if (cached)
            return cached;
        const result = evaluateCsgPlan(plan, csg, (contribution) => {
            const registration = nodeComponentRegistry.getCsgShape(contribution.type);
            if (!registration?.geometry)
                return null;
            return registration.geometry(contribution.node.properties, context);
        });
        if (result)
            setCachedEvaluation(plan.cacheKey, result);
        return result;
    }, [csg, isLoading, plan, context]);
    const status = loadFailed
        ? 'failed'
        : !csg || isLoading
            ? 'pending'
            : evaluation === null
                ? 'failed'
                : 'ready';
    // The library is a lazy chunk outside the resource bus. Counting it as pending lets the
    // camera's settle-fit frame the evaluated result, whenever the chunk lands.
    usePendingWhile(status === 'pending');
    const subtree = useMemo(() => ({ status, absorbedPaths: plan.absorbedPaths, invisiblePaths: plan.invisiblePaths, context }), [status, plan.absorbedPaths, plan.invisiblePaths, context]);
    // Resolve each output surface to a material slot. One component per slot keeps each
    // slot's `useResource` calls one set per component, so rules of hooks holds for any
    // surface count.
    const surfaces = useMemo(() => evaluation?.materials.map((address) => resolveMaterialSource(address, context)) ?? [], [evaluation, context]);
    const drawable = evaluation !== null && evaluation.geometry.getAttribute('position')?.count !== 0;
    return (_jsxs(_Fragment, { children: [drawable && (_jsxs("mesh", { ref: meshRef, castShadow: shadow.castShadow, onBeforeRender: shadow.onBeforeRender, onAfterRender: shadow.onAfterRender, onBeforeShadow: shadow.onBeforeShadow, onAfterShadow: shadow.onAfterShadow, receiveShadow: true, userData: visualLayersUserData(layers), geometry: evaluation.geometry, children: [_jsx(SurfaceMaterialSlots, { sources: surfaces }), _jsx(NextPassChain, { meshRef: meshRef, sources: surfaces, drawsPoints: false, godotLayers: layers, 
                        // The result draws as the root's own instance: a chain link casts nothing
                        // and is posed by the root's hooks, which it shares here.
                        shadow: shadow })] })), _jsx(CsgSubtreeProvider, { value: subtree, children: children }), status !== 'ready' && fallback] }));
}
//# sourceMappingURL=CsgRootMesh.js.map